import { ConfirmEntry, ConfirmRate, ConfirmRoom, ConfirmStat, ConfirmSummary } from '~/types/availability';
import {
  CatalogRoom,
  ConflictResolution,
  ExtractedDateRange,
  ManualRoom,
  RateConflictInfo,
  StopsellExtraction,
} from '~/types/stopsell';

import { findRateConflict } from '~/utils/stopsellConflicts';
import { formatDateRange } from '~/utils/stopsellDateUtils';

export function rateConflictKey(roomIndex: number, dateId: string, rateId: string): string {
  return `${roomIndex}-${dateId}-${rateId}`;
}

export function buildStopsellConfirmSummary(
  extraction: StopsellExtraction,
  localDates: Array<Array<ExtractedDateRange>>,
  rateConflicts: Array<Array<Array<RateConflictInfo>>>,
  resolutions: Record<string, ConflictResolution>,
  catalogRooms: Array<CatalogRoom>,
  roomTypeOverrides: Record<number, string>,
  manualRooms?: Array<ManualRoom>,
  manualResolutions?: Record<string, ConflictResolution>,
): ConfirmSummary {
  const rooms: Array<ConfirmRoom> = [];

  const stats = {
    rooms: 0,
    rates: new Set<string>(),
    new: 0,
    override: 0,
    extend: 0,
    ignored: 0,
  };

  function buildRoomRates(
    dates: Array<ExtractedDateRange>,
    roomIndex: number,
    catalogRoom: CatalogRoom,
    resolutionMap: Record<string, ConflictResolution>,
    conflictRows?: Array<Array<RateConflictInfo>>,
  ): Array<ConfirmRate> {
    const rates = catalogRoom.roomRates ?? [];
    const rateEntries: Map<string, Array<ConfirmEntry>> = new Map();

    dates.forEach((date, dateIndex) => {
      if (!date.selected) return;

      const dateConflicts =
        conflictRows?.[dateIndex] ??
        rates.map((rate) =>
          findRateConflict({ start: date.start, end: date.end }, rate.id, catalogRoom.blackoutDates ?? []),
        );

      rates.forEach((rate, rateIndex) => {
        const conflict = dateConflicts[rateIndex];
        if (!conflict) return;

        const resolution = resolutionMap[rateConflictKey(roomIndex, date.id, rate.id)] ?? null;

        let action: ConfirmEntry['action'] = 'new';
        let detail: string | undefined;

        if (conflict.status === 'covered_by_existing') {
          if (!resolution || resolution === 'skipped') {
            action = 'ignored';
          }
        } else if (conflict.status === 'clear') {
          action = 'new';
        } else if (conflict.status === 'covers_existing' || conflict.status === 'partial_overlap') {
          if (resolution === 'overriding' && conflict.existingRange) {
            action = 'override';
            detail = `deletes ${formatDateRange(conflict.existingRange.start, conflict.existingRange.end)} block`;
          } else if (resolution === 'extending' && conflict.mergedRange && conflict.existingRange) {
            action = 'extend';
            detail = `extends ${formatDateRange(conflict.existingRange.start, conflict.existingRange.end)} to ${formatDateRange(
              conflict.mergedRange.start,
              conflict.mergedRange.end,
            )}`;
          } else if (resolution === 'ignored' || !resolution) {
            action = 'ignored';
          }
        }

        if (!rateEntries.has(rate.id)) {
          rateEntries.set(rate.id, []);
        }
        rateEntries.get(rate.id)!.push({
          start: date.start,
          end: date.end,
          action,
          detail,
        });

        if (action === 'new') stats.new += 1;
        if (action === 'override') stats.override += 1;
        if (action === 'extend') stats.extend += 1;
        if (action === 'ignored') stats.ignored += 1;
      });
    });

    const confirmRates: Array<ConfirmRate> = [];
    rates.forEach((rate) => {
      const entries = rateEntries.get(rate.id) ?? [];
      if (entries.length === 0) return;

      stats.rates.add(rate.name);
      confirmRates.push({
        rateName: rate.name,
        entries,
      });
    });

    return confirmRates;
  }

  extraction.rooms.forEach((room, roomIndex) => {
    const dates = localDates[roomIndex] ?? [];
    if (!dates.some((d) => d.selected)) return;

    const roomTypeId = roomTypeOverrides[roomIndex] ?? room.roomTypeId;
    if (!roomTypeId) return;

    const catalogRoom = catalogRooms.find((c) => c.id === roomTypeId);
    if (!catalogRoom) return;

    const rates = buildRoomRates(dates, roomIndex, catalogRoom, resolutions, rateConflicts[roomIndex]);
    if (rates.length === 0) return;

    stats.rooms += 1;
    rooms.push({
      roomName: room.name,
      catalogRoomName: catalogRoom.name,
      rates,
    });
  });

  if (manualRooms) {
    const effectiveManualResolutions = manualResolutions ?? {};
    manualRooms.forEach((room, manualRoomIndex) => {
      const dates = room.dates.filter((d) => d.selected);
      if (dates.length === 0) return;

      const catalogRoom = catalogRooms.find((c) => c.id === room.catalogRoomId);
      if (!catalogRoom) return;

      const rates = buildRoomRates(dates, manualRoomIndex, catalogRoom, effectiveManualResolutions);
      if (rates.length === 0) return;

      stats.rooms += 1;
      rooms.push({
        roomName: room.catalogRoomName,
        catalogRoomName: catalogRoom.name,
        rates,
      });
    });
  }

  const confirmStats: Array<ConfirmStat> = [
    { label: 'ROOMS', value: stats.rooms },
    { label: 'RATES', value: stats.rates.size },
    { label: 'NEW RANGES', value: stats.new },
    { label: 'OVERRIDES', value: stats.override, color: 'warning' },
    { label: 'EXTENSIONS', value: stats.extend, color: 'success' },
    { label: 'IGNORED', value: stats.ignored },
  ];

  return {
    rooms,
    stats: confirmStats,
  };
}
